import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingShell from '@/components/landing/LandingShell';
import SEO from '@/components/common/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useRouteSsrData } from '@/lib/ssr/context';
import type { BugReportsPageData } from '@/types/ssr';
import { APP_VERSION } from '@/utils/version';
import {
  Bug,
  CircleCheckBig,
  Clock3,
  Loader2,
  LogIn,
  ShieldAlert,
  UploadCloud,
  X,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { Checkbox } from '@/components/ui/checkbox';
import { getBugsSeo } from '@/lib/seo/publicMetadata';
import { formatUtcDateTime } from '@/lib/ssr/date';

type Severity = 'low' | 'medium' | 'high' | 'critical';
type BugStatus = 'new' | 'active' | 'closed';
type MapleOSEdition = 'MapleOS' | 'MapleOS Pro' | 'MapleOS Enterprise';
type RuntimeEnvironment = 'browser' | 'native_desktop' | 'bare_metal';
type NativeOperatingSystem =
  | 'MapleOS Bare Metal'
  | 'Microsoft Windows 10'
  | 'Microsoft Windows 11'
  | 'Microsoft Windows Not 10/11'
  | 'Debian Based Distro'
  | 'RPM Based Distro'
  | 'Apple OSX'
  | 'Other';
type BrowserOption = 'Google Chrome' | 'Microsoft Edge' | 'Firefox' | 'Opera' | 'Brave' | 'Other';

type BugTrackerItem = {
  id: string;
  title: string;
  description: string;
  severity: Severity;
  status: BugStatus;
  version: string | null;
  updated_at: string | null;
  created_at: string;
  os: string | null;
  browser: string | null;
  time_occurred: string | null;
  runtime_environment: RuntimeEnvironment;
  mapleos_edition: MapleOSEdition;
  screenshot_video_url: string | null;
  admin_reply: string | null;
  admin_reply_updated_at: string | null;
  user_id: string | null;
};

type AdminDraft = {
  title: string;
  description: string;
  severity: Severity;
  status: BugStatus;
  adminReply: string;
};

type AdminQueueFilter = 'all' | 'opened' | 'active' | 'closed';
type AdminQueueSort = 'updated_desc' | 'updated_asc' | 'title_asc';

const ADMIN_QUEUE_FILTER_STORAGE_KEY = 'bug-reports-admin-queue-filter';
const ADMIN_QUEUE_SORT_STORAGE_KEY = 'bug-reports-admin-queue-sort';

const MAPLEOS_EDITIONS: MapleOSEdition[] = ['MapleOS', 'MapleOS Pro', 'MapleOS Enterprise'];
const NATIVE_OS_OPTIONS: NativeOperatingSystem[] = [
  'MapleOS Bare Metal',
  'Microsoft Windows 10',
  'Microsoft Windows 11',
  'Microsoft Windows Not 10/11',
  'Debian Based Distro',
  'RPM Based Distro',
  'Apple OSX',
  'Other',
];
const BROWSER_OPTIONS: BrowserOption[] = [
  'Google Chrome',
  'Microsoft Edge',
  'Firefox',
  'Opera',
  'Brave',
  'Other',
];

const severityBadgeClassNames: Record<Severity, string> = {
  low: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  medium: 'border-sky-500/30 bg-sky-500/10 text-sky-300',
  high: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  critical: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
};

const statusBadgeClassNames: Record<BugStatus, string> = {
  new: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
  active: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  closed: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
};

const detectNativeOperatingSystem = (): NativeOperatingSystem => {
  const nav = navigator as Navigator & {
    userAgentData?: {
      platform?: string;
      platformVersion?: string;
    };
  };
  const userAgent = navigator.userAgent;
  const platform = nav.userAgentData?.platform || navigator.platform || userAgent;
  const platformVersion = Number.parseInt(nav.userAgentData?.platformVersion?.split('.')[0] ?? '', 10);

  if (/Windows/i.test(platform) || /Windows/i.test(userAgent)) {
    if (!Number.isNaN(platformVersion)) {
      return platformVersion >= 13 ? 'Microsoft Windows 11' : 'Microsoft Windows 10';
    }

    if (/Windows NT 10\.0/i.test(userAgent)) {
      return 'Microsoft Windows 10';
    }

    return 'Microsoft Windows Not 10/11';
  }

  if (/Ubuntu|Debian|Mint|Pop!_OS|Pop_OS|Kubuntu|Xubuntu|Lubuntu|Zorin|Elementary|MX Linux|Kali/i.test(userAgent)) {
    return 'Debian Based Distro';
  }

  if (/Fedora|Red Hat|RHEL|CentOS|Rocky|AlmaLinux|SUSE|openSUSE/i.test(userAgent)) {
    return 'RPM Based Distro';
  }

  if (/Macintosh|Mac OS X|MacIntel|MacPPC|Mac68K/i.test(platform) || /Macintosh|Mac OS X/i.test(userAgent)) {
    return 'Apple OSX';
  }

  return 'Other';
};

const detectBrowser = (): BrowserOption => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Edg/')) return 'Microsoft Edge';
  if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) return 'Opera';
  if (userAgent.includes('Brave/')) return 'Brave';
  if (userAgent.includes('Firefox/')) return 'Firefox';
  if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) return 'Google Chrome';

  return 'Other';
};

const getRuntimeOptions = (edition: MapleOSEdition) => {
  const base = [
    { value: 'browser' as const, label: 'Browser' },
    { value: 'native_desktop' as const, label: 'Native desktop application' },
  ];

  if (edition === 'MapleOS') {
    return base;
  }

  return [...base, { value: 'bare_metal' as const, label: 'Bare metal install' }];
};

const formatDateTime = (value: string | null) => {
  if (!value) return '—';

  return formatUtcDateTime(value);
};

const toTrackerItem = (item: any): BugTrackerItem => ({
  id: item.id,
  title: item.title || 'Untitled bug report',
  description: item.description || '',
  severity: (item.severity || 'medium') as Severity,
  status: (item.status || 'new') as BugStatus,
  version: item.version || null,
  updated_at: item.updated_at || null,
  created_at: item.created_at,
  os: item.os || null,
  browser: item.browser || null,
  time_occurred: item.time_occurred || null,
  runtime_environment: (item.runtime_environment || 'browser') as RuntimeEnvironment,
  mapleos_edition: (item.mapleos_edition || 'MapleOS') as MapleOSEdition,
  screenshot_video_url: item.screenshot_video_url || null,
  admin_reply: item.admin_reply || null,
  admin_reply_updated_at: item.admin_reply_updated_at || null,
  user_id: item.user_id || null,
});

const createAdminDraft = (issue: BugTrackerItem | null): AdminDraft => ({
  title: issue?.title || '',
  description: issue?.description || '',
  severity: issue?.severity || 'medium',
  status: issue?.status || 'new',
  adminReply: issue?.admin_reply || '',
});

const IssueTable = ({ 
  items, 
  emptyText, 
  isAdmin = false,
  deletingIssueIds = [],
  onDeleteIssue,
}: {
  items: BugTrackerItem[];
  emptyText: string;
  isAdmin?: boolean;
  deletingIssueIds?: string[];
  onDeleteIssue?: (issue: BugTrackerItem) => void;
}) => {
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-muted-foreground">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10 bg-background/60 backdrop-blur-sm">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead>Issue</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Updated</TableHead>
            {isAdmin ? <TableHead className="w-[72px] text-right">Delete</TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id} className="border-white/10 align-top hover:bg-white/[0.03]">
              <TableCell className="min-w-[18rem] sm:min-w-[22rem]">
                <div className="space-y-2">
                  <div className="font-medium text-foreground">{item.title}</div>
                  <p className="line-clamp-3 max-w-xl text-sm text-muted-foreground">{item.description}</p>
                  {item.admin_reply ? (
                    <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">Admin reply:</span> {item.admin_reply}
                    </div>
                  ) : null}
                  {item.screenshot_video_url ? (
                    <a
                      href={item.screenshot_video_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex text-sm text-primary hover:underline"
                    >
                      View attachment
                    </a>
                  ) : null}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={severityBadgeClassNames[item.severity]}>
                  {item.severity}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={statusBadgeClassNames[item.status]}>
                  {item.status}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">{item.version || '—'}</TableCell>
              <TableCell className="text-muted-foreground">{formatDateTime(item.updated_at || item.created_at)}</TableCell>
              {isAdmin ? (
                <TableCell className="text-right">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => onDeleteIssue?.(item)}
                    disabled={deletingIssueIds.includes(item.id)}
                    aria-label={`Delete ${item.title}`}
                  >
                    {deletingIssueIds.includes(item.id) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const BugReports = () => {
  const initialData = useRouteSsrData<BugReportsPageData>('bugs');
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const currentVersion = `v${APP_VERSION}`;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = (profile?.role || '').toLowerCase() === 'admin';

  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [mapleosEdition, setMapleosEdition] = useState<MapleOSEdition>('MapleOS');
  const [runtimeEnvironment, setRuntimeEnvironment] = useState<RuntimeEnvironment>('browser');
  const [nativeOperatingSystem, setNativeOperatingSystem] = useState<NativeOperatingSystem | ''>('');
  const [browserDetails, setBrowserDetails] = useState<BrowserOption>('Other');
  const [timeOccurred, setTimeOccurred] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuesLoading, setIssuesLoading] = useState(() => !initialData);
  const [issues, setIssues] = useState<BugTrackerItem[]>(() => ((initialData?.issues || []).map(toTrackerItem) as BugTrackerItem[]));

  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [adminDraft, setAdminDraft] = useState<AdminDraft>(createAdminDraft(null));
  const [isSavingAdminIssue, setIsSavingAdminIssue] = useState(false);
  const [adminQueueFilter, setAdminQueueFilter] = useState<AdminQueueFilter>('all');
  const [adminQueueSort, setAdminQueueSort] = useState<AdminQueueSort>('updated_desc');
  const [selectedIssueIds, setSelectedIssueIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<BugStatus>('closed');
  const [isBulkUpdatingIssues, setIsBulkUpdatingIssues] = useState(false);
  const [isDeletingIssue, setIsDeletingIssue] = useState(false);
  const [isBulkDeletingIssues, setIsBulkDeletingIssues] = useState(false);
  const [deletingRowIssueIds, setDeletingRowIssueIds] = useState<string[]>([]);
  const [adminSearchQuery, setAdminSearchQuery] = useState('');

  const newIssues = useMemo(() => issues.filter((issue) => issue.status === 'new'), [issues]);
  const activeIssues = useMemo(() => issues.filter((issue) => issue.status === 'active'), [issues]);
  const closedIssues = useMemo(() => issues.filter((issue) => issue.status === 'closed'), [issues]);
  const runtimeOptions = useMemo(() => getRuntimeOptions(mapleosEdition), [mapleosEdition]);
  const needsBrowserDetails = runtimeEnvironment === 'browser';
  const adminVisibleIssues = useMemo(() => {
    const normalizedQuery = adminSearchQuery.trim().toLowerCase();
    const filtered = issues.filter((issue) => {
      const matchesFilter =
        adminQueueFilter === 'all'
          ? true
          : adminQueueFilter === 'opened'
            ? issue.status === 'new'
            : issue.status === adminQueueFilter;

      if (!matchesFilter) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const searchableText = [
        issue.id,
        issue.title,
        issue.description,
        issue.status,
        issue.severity,
        issue.version,
        issue.os,
        issue.browser,
        issue.mapleos_edition,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });

    return [...filtered].sort((a, b) => {
      if (adminQueueSort === 'updated_asc') {
        return new Date(a.updated_at || a.created_at).getTime() - new Date(b.updated_at || b.created_at).getTime();
      }

      if (adminQueueSort === 'title_asc') {
        return a.title.localeCompare(b.title);
      }

      return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
    });
  }, [issues, adminQueueFilter, adminQueueSort, adminSearchQuery]);
  const selectedIssue = useMemo(
    () => adminVisibleIssues.find((issue) => issue.id === selectedIssueId) || null,
    [adminVisibleIssues, selectedIssueId],
  );
  const allVisibleIssueIds = useMemo(() => adminVisibleIssues.map((issue) => issue.id), [adminVisibleIssues]);
  const allVisibleSelected = allVisibleIssueIds.length > 0 && allVisibleIssueIds.every((id) => selectedIssueIds.includes(id));
  const someVisibleSelected = allVisibleIssueIds.some((id) => selectedIssueIds.includes(id));

  useEffect(() => {
    if (mapleosEdition === 'MapleOS' && runtimeEnvironment === 'bare_metal') {
      setRuntimeEnvironment('browser');
    }
  }, [mapleosEdition, runtimeEnvironment]);

  useEffect(() => {
    if (runtimeEnvironment === 'bare_metal') {
      setNativeOperatingSystem('MapleOS Bare Metal');
      setBrowserDetails('Other');
      return;
    }

    if (nativeOperatingSystem === 'MapleOS Bare Metal') {
      setNativeOperatingSystem(detectNativeOperatingSystem());
    }

    if (runtimeEnvironment !== 'browser') {
      setBrowserDetails('Other');
    }
  }, [runtimeEnvironment, nativeOperatingSystem]);

  useEffect(() => {
    if (!isAdmin) {
      setSelectedIssueId(null);
      setAdminDraft(createAdminDraft(null));
      return;
    }

    if (issues.length === 0) {
      setSelectedIssueId(null);
      setAdminDraft(createAdminDraft(null));
      return;
    }

    if (adminVisibleIssues.length === 0) {
      setSelectedIssueId(null);
      setAdminDraft(createAdminDraft(null));
      return;
    }

    if (!selectedIssueId || !adminVisibleIssues.some((issue) => issue.id === selectedIssueId)) {
      setSelectedIssueId(adminVisibleIssues[0].id);
    }
  }, [isAdmin, issues, adminVisibleIssues, selectedIssueId]);

  useEffect(() => {
    setAdminDraft(createAdminDraft(selectedIssue));
  }, [selectedIssue]);

  useEffect(() => {
    setSelectedIssueIds((previous) => previous.filter((id) => allVisibleIssueIds.includes(id)));
  }, [allVisibleIssueIds]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    window.localStorage.setItem(ADMIN_QUEUE_FILTER_STORAGE_KEY, adminQueueFilter);
  }, [isAdmin, adminQueueFilter]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    window.localStorage.setItem(ADMIN_QUEUE_SORT_STORAGE_KEY, adminQueueSort);
  }, [isAdmin, adminQueueSort]);

  const resetForm = () => {
    setTitle('');
    setSeverity('medium');
    setMapleosEdition('MapleOS');
    setRuntimeEnvironment('browser');
    setNativeOperatingSystem(detectNativeOperatingSystem());
    setBrowserDetails(detectBrowser());
    setTimeOccurred(new Date().toISOString().slice(0, 16));
    setDescription('');
    setFile(null);
    setIsDraggingFile(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelection = (nextFile: File | null) => {
    if (!nextFile) {
      setFile(null);
      return;
    }

    if (!nextFile.type.startsWith('image/') && !nextFile.type.startsWith('video/')) {
      toast({
        title: 'Unsupported file type',
        description: 'Please use an image or video file.',
        variant: 'destructive',
      });
      return;
    }

    setFile(nextFile);
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    handleFileSelection(event.dataTransfer.files?.[0] || null);
  };

  const fetchIssues = async () => {
    setIssuesLoading(true);

    const bugReports = supabase.from('bug_reports') as any;
    let query = bugReports
      .select(
        'id, title, description, severity, status, version, updated_at, created_at, os, browser, time_occurred, runtime_environment, mapleos_edition, screenshot_video_url, admin_reply, admin_reply_updated_at, user_id',
      )
      .order('updated_at', { ascending: false });

    if (!isAdmin) {
      query = query.in('status', ['active', 'closed']);
    }

    const { data, error } = await query;

    if (error) {
      toast({
        title: 'Could not load issue tracker',
        description: error.message,
        variant: 'destructive',
      });
      setIssues([]);
    } else {
      setIssues((data || []).map(toTrackerItem));
    }

    setIssuesLoading(false);
  };

  useEffect(() => {
    setNativeOperatingSystem(detectNativeOperatingSystem());
    setBrowserDetails(detectBrowser());
    setTimeOccurred(new Date().toISOString().slice(0, 16));
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [isAdmin]);

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please log in before submitting a bug report.',
        variant: 'destructive',
      });
      return;
    }

    if (!title.trim() || !nativeOperatingSystem || !timeOccurred || !description.trim()) {
      toast({
        title: 'Missing info',
        description: 'Please fill out all required fields.',
        variant: 'destructive',
      });
      return;
    }

    if (needsBrowserDetails && !browserDetails.trim()) {
      toast({
        title: 'Browser details required',
        description: 'Please tell us which browser you are using.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let uploadedFileURL: string | undefined;
      let uploadedFilePath: string | undefined;
      let mediaType: string | undefined;

      if (file) {
        const fileExtension = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExtension}`;
        const filePath = `bug-reports/${fileName}`;

        const { error: uploadError } = await supabase.storage.from('bug-media').upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

        if (uploadError) {
          throw new Error(uploadError.message);
        }

        const { data: publicUrlData } = supabase.storage.from('bug-media').getPublicUrl(filePath);
        uploadedFileURL = publicUrlData.publicUrl;
        uploadedFilePath = filePath;
        mediaType = 'file';
      }

      const bugReports = supabase.from('bug_reports') as any;
      const { error } = await bugReports.insert({
        title: title.trim(),
        severity,
        status: 'new',
        user_id: user.id,
        mapleos_edition: mapleosEdition,
        runtime_environment: runtimeEnvironment,
        os: nativeOperatingSystem,
        browser: needsBrowserDetails ? browserDetails.trim() : null,
        time_occurred: new Date(timeOccurred).toISOString(),
        version: currentVersion,
        media_type: mediaType,
        screenshot_video_url: uploadedFileURL,
        screenshot_video_path: uploadedFilePath,
        description: description.trim(),
        user_agent: navigator.userAgent,
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Bug report submitted',
        description: 'Thanks — your report is now in the review queue.',
      });

      resetForm();
      fetchIssues();
    } catch (error: any) {
      toast({
        title: 'Submission failed',
        description: error.message || 'Something went wrong while submitting your report.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAdminIssue = async () => {
    if (!isAdmin || !selectedIssue) {
      return;
    }

    if (!adminDraft.title.trim() || !adminDraft.description.trim()) {
      toast({
        title: 'Missing info',
        description: 'Title and description are required.',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingAdminIssue(true);

    try {
      const adminReply = adminDraft.adminReply.trim();
      const bugReports = supabase.from('bug_reports') as any;
      const { error } = await bugReports
        .update({
          title: adminDraft.title.trim(),
          description: adminDraft.description.trim(),
          severity: adminDraft.severity,
          status: adminDraft.status,
          admin_reply: adminReply || null,
          admin_reply_updated_at: adminReply ? new Date().toISOString() : null,
          admin_reply_user_id: adminReply ? user?.id || null : null,
        })
        .eq('id', selectedIssue.id);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Bug report updated',
        description: 'Your changes were saved successfully.',
      });

      fetchIssues();
    } catch (error: any) {
      toast({
        title: 'Save failed',
        description: error.message || 'Could not save changes to this bug report.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingAdminIssue(false);
    }
  };

  const toggleIssueSelection = (issueId: string, checked: boolean) => {
    setSelectedIssueIds((previous) => {
      if (checked) {
        return previous.includes(issueId) ? previous : [...previous, issueId];
      }

      return previous.filter((id) => id !== issueId);
    });
  };

  const handleToggleSelectAllVisible = (checked: boolean) => {
    if (checked) {
      setSelectedIssueIds(allVisibleIssueIds);
      return;
    }

    setSelectedIssueIds([]);
  };

  const handleBulkStatusUpdate = async () => {
    if (!isAdmin || selectedIssueIds.length === 0) {
      return;
    }

    setIsBulkUpdatingIssues(true);

    try {
      const bugReports = supabase.from('bug_reports') as any;
      const { error } = await bugReports.update({ status: bulkStatus }).in('id', selectedIssueIds);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Bulk update complete',
        description:
          bulkStatus === 'closed'
            ? `Closed ${selectedIssueIds.length} report${selectedIssueIds.length === 1 ? '' : 's'}.`
            : `Updated ${selectedIssueIds.length} report${selectedIssueIds.length === 1 ? '' : 's'} to ${bulkStatus}.`,
      });

      setSelectedIssueIds([]);
      fetchIssues();
    } catch (error: any) {
      toast({
        title: 'Bulk update failed',
        description: error.message || 'Could not update the selected reports.',
        variant: 'destructive',
      });
    } finally {
      setIsBulkUpdatingIssues(false);
    }
  };

  const handleDeleteIssue = async () => {
    if (!isAdmin || !selectedIssue) {
      return;
    }

    const confirmed = window.confirm(`Delete bug report "${selectedIssue.title}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setIsDeletingIssue(true);

    try {
      const bugReports = supabase.from('bug_reports') as any;
      const { error } = await bugReports.delete().eq('id', selectedIssue.id);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Bug report deleted',
        description: 'The selected report has been removed.',
      });

      setSelectedIssueIds((previous) => previous.filter((id) => id !== selectedIssue.id));
      fetchIssues();
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.message || 'Could not delete this bug report.',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingIssue(false);
    }
  };

  const handleQuickDeleteIssue = async (issue: BugTrackerItem) => {
    if (!isAdmin) {
      return;
    }

    const confirmed = window.confirm(`Delete bug report "${issue.title}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setDeletingRowIssueIds((previous) => [...previous, issue.id]);

    try {
      const bugReports = supabase.from('bug_reports') as any;
      const { error } = await bugReports.delete().eq('id', issue.id);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Bug report deleted',
        description: `Deleted "${issue.title}".`,
      });

      setSelectedIssueIds((previous) => previous.filter((id) => id !== issue.id));
      fetchIssues();
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.message || 'Could not delete this bug report.',
        variant: 'destructive',
      });
    } finally {
      setDeletingRowIssueIds((previous) => previous.filter((id) => id !== issue.id));
    }
  };

  const handleBulkDeleteIssues = async () => {
    if (!isAdmin || selectedIssueIds.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedIssueIds.length} selected bug report${selectedIssueIds.length === 1 ? '' : 's'}? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    setIsBulkDeletingIssues(true);

    try {
      const bugReports = supabase.from('bug_reports') as any;
      const { error } = await bugReports.delete().in('id', selectedIssueIds);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Bug reports deleted',
        description: `Deleted ${selectedIssueIds.length} report${selectedIssueIds.length === 1 ? '' : 's'}.`,
      });

      setSelectedIssueIds([]);
      fetchIssues();
    } catch (error: any) {
      toast({
        title: 'Bulk delete failed',
        description: error.message || 'Could not delete the selected reports.',
        variant: 'destructive',
      });
    } finally {
      setIsBulkDeletingIssues(false);
    }
  };

  return (
    <LandingShell>
      <SEO {...getBugsSeo()} />

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 sm:gap-8 sm:py-10 sm:px-6 lg:px-8">
        <section className="grid gap-4 sm:gap-6 lg:grid-cols-[1.35fr_0.65fr]">
          <Card className="border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.96),rgba(8,12,20,0.96))] shadow-[0_24px_80px_rgba(0,0,0,0.28)]">
            <CardHeader className="space-y-3 sm:space-y-4">
              <div className="space-y-3">
                <CardTitle className="flex items-center gap-3 text-2xl sm:text-3xl lg:text-4xl">
                  <Bug className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
                  Bug Reporting
                </CardTitle>
                <CardDescription className="max-w-2xl text-sm leading-7 sm:text-base text-muted-foreground">
                  Submit bug reports directly to our team, then monitor the public tracker for high-priority issues that are active or resolved.
                  {isAdmin ? ' You are viewing the admin management version of this page.' : ''}
                </CardDescription>
              </div>
            </CardHeader>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader className="pb-3">
                <CardDescription>Active issues</CardDescription>
                <CardTitle className="flex items-center gap-2 text-3xl">
                  <ShieldAlert className="h-7 w-7 text-amber-300" />
                  {issuesLoading ? '—' : activeIssues.length}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader className="pb-3">
                <CardDescription>Closed issues</CardDescription>
                <CardTitle className="flex items-center gap-2 text-3xl">
                  <CircleCheckBig className="h-7 w-7 text-emerald-300" />
                  {issuesLoading ? '—' : closedIssues.length}
                </CardTitle>
              </CardHeader>
            </Card>
            {isAdmin ? (
              <Card className="border-white/10 bg-white/[0.03] sm:col-span-2 lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardDescription>New reports awaiting review</CardDescription>
                  <CardTitle className="flex items-center gap-2 text-3xl">
                    <Clock3 className="h-7 w-7 text-slate-300" />
                    {issuesLoading ? '—' : newIssues.length}
                  </CardTitle>
                </CardHeader>
              </Card>
            ) : null}
          </div>
        </section>

        <Tabs defaultValue="report" className="space-y-6">
          <TabsList className="grid h-auto w-full grid-cols-2 gap-2 rounded-2xl bg-white/[0.04] p-2 sm:flex sm:flex-wrap sm:justify-start">
            <TabsTrigger value="report" className="h-auto w-full whitespace-normal rounded-xl px-3 py-2 text-xs leading-tight text-center sm:w-auto sm:px-4 sm:text-sm">Report a bug</TabsTrigger>
            <TabsTrigger value="active" className="h-auto w-full whitespace-normal rounded-xl px-3 py-2 text-xs leading-tight text-center sm:w-auto sm:px-4 sm:text-sm">Active issues ({activeIssues.length})</TabsTrigger>
            <TabsTrigger value="closed" className="h-auto w-full whitespace-normal rounded-xl px-3 py-2 text-xs leading-tight text-center sm:w-auto sm:px-4 sm:text-sm">Closed issues ({closedIssues.length})</TabsTrigger>
            {isAdmin ? (
              <TabsTrigger value="admin" className="h-auto w-full whitespace-normal rounded-xl px-3 py-2 text-xs leading-tight text-center sm:w-auto sm:px-4 sm:text-sm">Admin queue ({issues.length})</TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="report" className="mt-0">
            {user ? (
              <Card className="border-white/10 bg-background/70 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>Bug report form</CardTitle>
                  <CardDescription>
                    New submissions stay private while they are reviewed. Confirmed issues will move into the public tracker below.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="bug-title">Issue title</Label>
                      <Input
                        id="bug-title"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="Example: News article page returns not found"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bug-severity">Severity</Label>
                      <Select value={severity} onValueChange={(value) => setSeverity(value as Severity)}>
                        <SelectTrigger id="bug-severity">
                          <SelectValue placeholder="Select severity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bug-mapleos-edition">MapleOS edition</Label>
                      <Select value={mapleosEdition} onValueChange={(value) => setMapleosEdition(value as MapleOSEdition)}>
                        <SelectTrigger id="bug-mapleos-edition">
                          <SelectValue placeholder="Select MapleOS edition" />
                        </SelectTrigger>
                        <SelectContent>
                          {MAPLEOS_EDITIONS.map((edition) => (
                            <SelectItem key={edition} value={edition}>
                              {edition}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bug-runtime">Where are you running it?</Label>
                      <Select
                        value={runtimeEnvironment}
                        onValueChange={(value) => setRuntimeEnvironment(value as RuntimeEnvironment)}
                      >
                        <SelectTrigger id="bug-runtime">
                          <SelectValue placeholder="Select runtime" />
                        </SelectTrigger>
                        <SelectContent>
                          {runtimeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bug-os">Native Operating System</Label>
                      <Select
                        value={nativeOperatingSystem}
                        onValueChange={(value) => setNativeOperatingSystem(value as NativeOperatingSystem)}
                      >
                        <SelectTrigger id="bug-os">
                          <SelectValue placeholder="Select native operating system" />
                        </SelectTrigger>
                        <SelectContent>
                          {NATIVE_OS_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {needsBrowserDetails ? (
                      <div className="space-y-2">
                        <Label htmlFor="bug-browser">Browser details</Label>
                        <Select
                          value={browserDetails}
                          onValueChange={(value) => setBrowserDetails(value as BrowserOption)}
                        >
                          <SelectTrigger id="bug-browser">
                            <SelectValue placeholder="Select browser" />
                          </SelectTrigger>
                          <SelectContent>
                            {BROWSER_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-muted-foreground">
                        Runtime target:{' '}
                        <span className="font-medium text-foreground">
                          {runtimeOptions.find((option) => option.value === runtimeEnvironment)?.label}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
                    <div className="space-y-2">
                      <Label htmlFor="bug-time">When did it happen?</Label>
                      <Input
                        id="bug-time"
                        type="datetime-local"
                        value={timeOccurred}
                        onChange={(event) => setTimeOccurred(event.target.value)}
                      />
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-muted-foreground">
                      Build: <span className="font-medium text-foreground">{currentVersion}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bug-description">What went wrong?</Label>
                    <Textarea
                      id="bug-description"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      rows={8}
                      placeholder="Tell us what you were doing, what you expected, and what actually happened."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bug-file">Screenshot or video (optional)</Label>
                    <div
                      className={`rounded-xl border border-dashed p-4 transition-colors ${
                        isDraggingFile ? 'border-primary bg-primary/10' : 'border-white/10 bg-white/[0.03]'
                      }`}
                      onDragOver={(event) => {
                        event.preventDefault();
                        setIsDraggingFile(true);
                      }}
                      onDragLeave={() => setIsDraggingFile(false)}
                      onDrop={handleFileDrop}
                    >
                      <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                        <UploadCloud className="h-4 w-4" />
                        Drag an image or video here, or choose a file below.
                      </div>
                      <Input
                        id="bug-file"
                        type="file"
                        accept="image/*,video/*"
                        ref={fileInputRef}
                        onChange={(event) => handleFileSelection(event.target.files?.[0] || null)}
                      />
                    </div>
                    {file ? (
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-2">
                          <UploadCloud className="h-4 w-4" />
                          {file.name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-auto px-0 text-sm text-primary hover:bg-transparent"
                          onClick={() => {
                            setFile(null);
                            setIsDraggingFile(false);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                        >
                          Remove file
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
                    <p className="text-sm text-muted-foreground">
                      Only active and closed issues are shown publicly. New reports are reviewed first.
                    </p>
                    <Button onClick={handleSubmit} disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Submit report
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-white/10 bg-background/70 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>Sign in to submit a bug report</CardTitle>
                  <CardDescription>
                    The public tracker is open to everyone, but reporting a new bug requires an account so we can manage submissions safely.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-3">
                  <Button asChild>
                    <Link to="/auth">
                      <LogIn className="mr-2 h-4 w-4" />
                      Login to report a bug
                    </Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/news">Browse updates</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="active" className="mt-0 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold">Active issues</h2>
                <p className="text-sm text-muted-foreground">Major bugs currently being worked on or monitored.</p>
              </div>
              <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-300">
                <Clock3 className="mr-2 h-3.5 w-3.5" />
                In progress
              </Badge>
            </div>
            {issuesLoading ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-muted-foreground">
                Loading active issues...
              </div>
            ) : (
              <IssueTable
                items={activeIssues}
                emptyText="No active issues are being tracked publicly right now."
                isAdmin={isAdmin}
                deletingIssueIds={deletingRowIssueIds}
                onDeleteIssue={handleQuickDeleteIssue}
              />
            )}
          </TabsContent>

          <TabsContent value="closed" className="mt-0 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold">Closed issues</h2>
                <p className="text-sm text-muted-foreground">Recently resolved bugs that have been closed out.</p>
              </div>
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                <CircleCheckBig className="mr-2 h-3.5 w-3.5" />
                Resolved
              </Badge>
            </div>
            {issuesLoading ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-muted-foreground">
                Loading closed issues...
              </div>
            ) : (
              <IssueTable
                items={closedIssues}
                emptyText="No closed issues are listed yet."
                isAdmin={isAdmin}
                deletingIssueIds={deletingRowIssueIds}
                onDeleteIssue={handleQuickDeleteIssue}
              />
            )}
          </TabsContent>

          {isAdmin ? (
            <TabsContent value="admin" className="mt-0 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-semibold">Admin bug management</h2>
                  <p className="text-sm text-muted-foreground">
                    Review new submissions, edit report details, reply to reporters, and change issue status.
                  </p>
                </div>
                <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
                  Admin tools enabled
                </Badge>
              </div>

              {issuesLoading ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-muted-foreground">
                  Loading reports...
                </div>
              ) : issues.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-muted-foreground">
                  No bug reports have been submitted yet.
                </div>
              ) : (
                <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
                  <Card className="border-white/10 bg-background/70 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle>Reports</CardTitle>
                      <CardDescription>Select a report to review and update.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <label className="flex items-center gap-3 text-sm text-foreground">
                            <Checkbox
                              checked={allVisibleSelected || (someVisibleSelected ? 'indeterminate' : false)}
                              onCheckedChange={(checked) => handleToggleSelectAllVisible(Boolean(checked))}
                            />
                            Select all visible
                          </label>
                          <div className="text-sm text-muted-foreground">
                            {selectedIssueIds.length} selected
                          </div>
                        </div>

                        <div className="mt-4 space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="admin-bulk-status">Bulk status</Label>
                            <Select value={bulkStatus} onValueChange={(value) => setBulkStatus(value as BugStatus)}>
                              <SelectTrigger id="admin-bulk-status" className="w-full">
                                <SelectValue placeholder="Choose status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">Opened</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="grid gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              onClick={() => setSelectedIssueIds([])}
                              disabled={selectedIssueIds.length === 0 || isBulkUpdatingIssues || isBulkDeletingIssues}
                            >
                              Clear selection
                            </Button>
                            <Button
                              type="button"
                              className="w-full"
                              onClick={handleBulkStatusUpdate}
                              disabled={selectedIssueIds.length === 0 || isBulkUpdatingIssues || isBulkDeletingIssues}
                            >
                              {isBulkUpdatingIssues ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              {bulkStatus === 'closed' ? 'Close selected' : `Apply ${bulkStatus}`}
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              className="w-full"
                              onClick={handleBulkDeleteIssues}
                              disabled={selectedIssueIds.length === 0 || isBulkUpdatingIssues || isBulkDeletingIssues}
                            >
                              {isBulkDeletingIssues ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                              Delete selected
                            </Button>
                          </div>
                        </div>

                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="admin-queue-search">Search</Label>
                        <Input
                          id="admin-queue-search"
                          value={adminSearchQuery}
                          onChange={(event) => setAdminSearchQuery(event.target.value)}
                          placeholder="Search title, description, ID, version, OS..."
                        />
                      </div>

                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
                        <div className="space-y-2">
                          <Label htmlFor="admin-queue-filter">Filter</Label>
                          <Select
                            value={adminQueueFilter}
                            onValueChange={(value) => setAdminQueueFilter(value as AdminQueueFilter)}
                          >
                            <SelectTrigger id="admin-queue-filter">
                              <SelectValue placeholder="Filter reports" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All reports</SelectItem>
                              <SelectItem value="opened">Opened</SelectItem>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="admin-queue-sort">Sort</Label>
                          <Select
                            value={adminQueueSort}
                            onValueChange={(value) => setAdminQueueSort(value as AdminQueueSort)}
                          >
                            <SelectTrigger id="admin-queue-sort">
                              <SelectValue placeholder="Sort reports" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="updated_desc">Last updated (newest)</SelectItem>
                              <SelectItem value="updated_asc">Last updated (oldest)</SelectItem>
                              <SelectItem value="title_asc">Title A-Z</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="text-sm text-muted-foreground">
                        Showing {adminVisibleIssues.length} of {issues.length} reports
                      </div>

                      {adminVisibleIssues.length === 0 ? (
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-8 text-center text-sm text-muted-foreground">
                          No reports match the current filter or search.
                        </div>
                      ) : (
                        adminVisibleIssues.map((issue) => (
                          <div
                            key={issue.id}
                            className={`w-full rounded-xl border p-4 text-left transition-colors ${
                              selectedIssueId === issue.id
                                ? 'border-primary bg-primary/10'
                                : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.05]'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                className="mt-1"
                                checked={selectedIssueIds.includes(issue.id)}
                                onCheckedChange={(checked) => toggleIssueSelection(issue.id, Boolean(checked))}
                              />
                              <button
                                type="button"
                                onClick={() => setSelectedIssueId(issue.id)}
                                className="flex-1 text-left"
                              >
                                <div className="space-y-3">
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="font-medium text-foreground">{issue.title}</div>
                                    <Badge variant="outline" className={statusBadgeClassNames[issue.status]}>
                                      {issue.status}
                                    </Badge>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <Badge variant="outline" className={severityBadgeClassNames[issue.severity]}>
                                      {issue.severity}
                                    </Badge>
                                    <Badge variant="outline" className="border-white/10 bg-white/[0.03] text-muted-foreground">
                                      {issue.mapleos_edition}
                                    </Badge>
                                  </div>
                                  <p className="line-clamp-2 text-sm text-muted-foreground">{issue.description}</p>
                                  <div className="text-xs text-muted-foreground">
                                    Updated {formatDateTime(issue.updated_at || issue.created_at)}
                                  </div>
                                </div>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-white/10 bg-background/70 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle>Report details</CardTitle>
                      <CardDescription>
                        {selectedIssue ? `Editing report ${selectedIssue.id}` : 'Select a report to begin.'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {selectedIssue ? (
                        <>
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                              <div className="text-muted-foreground">Reporter</div>
                              <div className="mt-1 break-all text-foreground">{selectedIssue.user_id || '—'}</div>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                              <div className="text-muted-foreground">Occurred</div>
                              <div className="mt-1 text-foreground">{formatDateTime(selectedIssue.time_occurred)}</div>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                              <div className="text-muted-foreground">Build</div>
                              <div className="mt-1 text-foreground">{selectedIssue.version || '—'}</div>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                              <div className="text-muted-foreground">Edition</div>
                              <div className="mt-1 text-foreground">{selectedIssue.mapleos_edition}</div>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                              <div className="text-muted-foreground">Runtime</div>
                              <div className="mt-1 text-foreground">{selectedIssue.runtime_environment}</div>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                              <div className="text-muted-foreground">OS / Browser</div>
                              <div className="mt-1 text-foreground">
                                {selectedIssue.os || '—'}
                                {selectedIssue.browser ? ` • ${selectedIssue.browser}` : ''}
                              </div>
                            </div>
                          </div>

                          {selectedIssue.screenshot_video_url ? (
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                              <div className="text-muted-foreground">Attachment</div>
                              <a
                                href={selectedIssue.screenshot_video_url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-1 inline-flex text-primary hover:underline"
                              >
                                Open uploaded image/video
                              </a>
                            </div>
                          ) : null}

                          <div className="grid gap-6 md:grid-cols-2">
                            <div className="space-y-2 md:col-span-2">
                              <Label htmlFor="admin-bug-title">Issue title</Label>
                              <Input
                                id="admin-bug-title"
                                value={adminDraft.title}
                                onChange={(event) => setAdminDraft((prev) => ({ ...prev, title: event.target.value }))}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="admin-bug-severity">Severity</Label>
                              <Select
                                value={adminDraft.severity}
                                onValueChange={(value) =>
                                  setAdminDraft((prev) => ({ ...prev, severity: value as Severity }))
                                }
                              >
                                <SelectTrigger id="admin-bug-severity">
                                  <SelectValue placeholder="Select severity" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="low">Low</SelectItem>
                                  <SelectItem value="medium">Medium</SelectItem>
                                  <SelectItem value="high">High</SelectItem>
                                  <SelectItem value="critical">Critical</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="admin-bug-status">Status</Label>
                              <Select
                                value={adminDraft.status}
                                onValueChange={(value) =>
                                  setAdminDraft((prev) => ({ ...prev, status: value as BugStatus }))
                                }
                              >
                                <SelectTrigger id="admin-bug-status">
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2 md:col-span-2">
                              <Label htmlFor="admin-bug-description">Description</Label>
                              <Textarea
                                id="admin-bug-description"
                                rows={8}
                                value={adminDraft.description}
                                onChange={(event) =>
                                  setAdminDraft((prev) => ({ ...prev, description: event.target.value }))
                                }
                              />
                            </div>

                            <div className="space-y-2 md:col-span-2">
                              <Label htmlFor="admin-bug-reply">Admin reply</Label>
                              <Textarea
                                id="admin-bug-reply"
                                rows={6}
                                value={adminDraft.adminReply}
                                onChange={(event) =>
                                  setAdminDraft((prev) => ({ ...prev, adminReply: event.target.value }))
                                }
                                placeholder="Reply to the reporter or add a public progress update..."
                              />
                              <p className="text-sm text-muted-foreground">
                                This reply is visible on the bug report for admins and for any public issue you mark active or closed.
                                {selectedIssue.admin_reply_updated_at
                                  ? ` Last updated ${formatDateTime(selectedIssue.admin_reply_updated_at)}.`
                                  : ''}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
                            <div className="text-sm text-muted-foreground">
                              Last updated {formatDateTime(selectedIssue.updated_at || selectedIssue.created_at)}
                            </div>
                            <div className="flex flex-wrap gap-3">
                              <Button
                                type="button"
                                variant="destructive"
                                onClick={handleDeleteIssue}
                                disabled={isDeletingIssue || isSavingAdminIssue}
                              >
                                {isDeletingIssue ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Delete bug
                              </Button>
                              <Button type="button" variant="outline" onClick={fetchIssues}>
                                Refresh list
                              </Button>
                              <Button type="button" onClick={handleSaveAdminIssue} disabled={isSavingAdminIssue}>
                                {isSavingAdminIssue ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Save changes
                              </Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-muted-foreground">
                          Select a bug report from the list to review it.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          ) : null}
        </Tabs>
      </div>
    </LandingShell>
  );
};

export default BugReports;